import { useEffect, useState, useMemo, useRef } from "react";
import { apiClient, ApiError } from "../../lib/api-client";
import { formatBRL } from "../../lib/currency";
import { CurrencyInput } from "../../components/CurrencyInput";
import { usePagination } from "../../hooks/usePagination";
import { Pagination } from "../../components/Pagination";

interface AuthorItem {
  id: string;
  name: string;
  slug?: string;
  bio?: string | null;
}

interface CategoryItem {
  id: string;
  name: string;
  slug: string;
}

interface CollectionItem {
  id: string;
  title: string;
  slug: string;
}

interface AdminCatalogItem {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  sku: string;
  isbn: string | null;
  cover_r2_key: string | null;
  is_available: boolean;
  is_free: boolean;
  category_id: string | null;
  category_name: string | null;
  collection_id: string | null;
  collection_title: string | null;
  price_cents: number;
  promo_price_cents: number | null;
  authors: AuthorItem[];
  formats: string[];
  created_at: string;
}

export function AdminCatalogPage() {
  const [items, setItems] = useState<AdminCatalogItem[]>([]);
  const [authors, setAuthors] = useState<AuthorItem[]>([]);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [collections, setCollections] = useState<CollectionItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Filtro e Busca
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  // Formulário de Criação
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: "",
    subtitle: "",
    isbn: "",
    sku: "",
    slug: "",
    priceCents: 4990,
    promoPriceCents: null as number | null,
    categoryId: "",
    collectionId: "",
    selectedAuthorIds: [] as string[],
    coverFile: null as File | null,
    pdfFile: null as File | null,
    epubFile: null as File | null,
  });

  const createCoverInputRef = useRef<HTMLInputElement>(null);
  const createPdfInputRef = useRef<HTMLInputElement>(null);
  const createEpubInputRef = useRef<HTMLInputElement>(null);

  // Modal de Edição Completa
  const [editingItem, setEditingItem] = useState<AdminCatalogItem | null>(null);
  const [editForm, setEditForm] = useState({
    title: "",
    subtitle: "",
    isbn: "",
    sku: "",
    slug: "",
    priceCents: 0,
    promoPriceCents: null as number | null,
    categoryId: "",
    collectionId: "",
    selectedAuthorIds: [] as string[],
    isAvailable: true,
    coverFile: null as File | null,
    pdfFile: null as File | null,
    epubFile: null as File | null,
  });

  const editCoverInputRef = useRef<HTMLInputElement>(null);
  const editPdfInputRef = useRef<HTMLInputElement>(null);
  const editEpubInputRef = useRef<HTMLInputElement>(null);

  // Modal Rápido de Preço
  const [priceModalItem, setPriceModalItem] = useState<AdminCatalogItem | null>(null);
  const [quickPriceCents, setQuickPriceCents] = useState(0);
  const [quickPromoPriceCents, setQuickPromoPriceCents] = useState<number | null>(null);

  // Modal para Criar Novo Autor Inline
  const [showNewAuthorModal, setShowNewAuthorModal] = useState(false);
  const [newAuthorName, setNewAuthorName] = useState("");
  const [newAuthorBio, setNewAuthorBio] = useState("");
  const [authorTarget, setAuthorTarget] = useState<"create" | "edit">("create");

  // Modal para Criar Nova Categoria Inline
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryParentId, setNewCategoryParentId] = useState("");
  const [categoryTarget, setCategoryTarget] = useState<"create" | "edit">("create");

  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);

  function loadAll() {
    setLoading(true);
    Promise.all([
      apiClient.get<{ items: AdminCatalogItem[] }>("/admin/catalog"),
      apiClient.get<{ items: AuthorItem[] }>("/admin/authors").catch(() => ({ items: [] })),
      apiClient.get<{ items: CategoryItem[] }>("/admin/categories").catch(() => ({ items: [] })),
      apiClient.get<{ items: CollectionItem[] }>("/admin/collections").catch(() => ({ items: [] })),
    ])
      .then(([catData, authData, catListData, colData]) => {
        setItems(catData.items || []);
        setAuthors(authData.items || []);
        setCategories(catListData.items || []);
        setCollections(colData.items || []);
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : "Erro ao carregar dados do catálogo.");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadAll();
  }, []);

  // Filtragem local instantânea
  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return items.filter((item) => {
      // Filtro de status
      if (statusFilter === "active" && !item.is_available) return false;
      if (statusFilter === "inactive" && item.is_available) return false;

      // Filtro por texto: título, autor, isbn, sku
      if (!query) return true;

      const matchTitle = item.title.toLowerCase().includes(query);
      const matchSubtitle = item.subtitle?.toLowerCase().includes(query) ?? false;
      const matchIsbn = item.isbn?.toLowerCase().includes(query) ?? false;
      const matchSku = item.sku.toLowerCase().includes(query);
      const matchCategory = item.category_name?.toLowerCase().includes(query) ?? false;
      const matchAuthors = item.authors.some((a) => a.name.toLowerCase().includes(query));

      return matchTitle || matchSubtitle || matchIsbn || matchSku || matchCategory || matchAuthors;
    });
  }, [items, searchQuery, statusFilter]);

  const catalogPagination = usePagination(filteredItems, 15);

  // Ativar / Desativar Obra
  async function handleToggleStatus(item: AdminCatalogItem) {
    const newStatus = !item.is_available;
    setMessage(null);
    setError(null);
    try {
      await apiClient.patch(`/admin/catalog/${item.id}`, { isAvailable: newStatus });
      setItems((prev) =>
        prev.map((it) => (it.id === item.id ? { ...it, is_available: newStatus } : it)),
      );
      setMessage(`Obra "${item.title}" agora está ${newStatus ? "ATIVADA" : "DESATIVADA"}.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao alterar disponibilidade da obra.");
    }
  }

  // Criar Nova Obra com Upload de Arquivos
  async function handleCreateBook(e: React.FormEvent) {
    e.preventDefault();
    setMessage(null);
    setError(null);
    setUploadProgress(null);
    setSubmitting(true);

    try {
      const priceCents = createForm.priceCents;
      const promoPriceCents = createForm.promoPriceCents ?? undefined;

      const res = await apiClient.post<{ id: string }>("/admin/catalog", {
        title: createForm.title.trim(),
        subtitle: createForm.subtitle.trim() || undefined,
        isbn: createForm.isbn.trim() || undefined,
        sku: createForm.sku.trim() || undefined,
        slug: createForm.slug.trim() || undefined,
        priceCents,
        promoPriceCents,
        categoryId: createForm.categoryId || undefined,
        collectionId: createForm.collectionId || undefined,
        authorIds: createForm.selectedAuthorIds,
        isAvailable: true,
      });

      // Se houver arquivos selecionados (Capa, PDF, EPUB), faz upload imediato
      if (createForm.coverFile || createForm.pdfFile || createForm.epubFile) {
        setUploadProgress("Enviando arquivos digitais para o Cloudflare R2…");
        const fd = new FormData();
        if (createForm.coverFile) fd.append("cover", createForm.coverFile);
        if (createForm.pdfFile) fd.append("pdf", createForm.pdfFile);
        if (createForm.epubFile) fd.append("epub", createForm.epubFile);

        await apiClient.postForm(`/admin/catalog/${res.id}/upload`, fd);
      }

      setMessage("Livro e arquivos digitais cadastrados com sucesso no catálogo!");
      setCreateForm({
        title: "",
        subtitle: "",
        isbn: "",
        sku: "",
        slug: "",
        priceCents: 4990,
        promoPriceCents: null,
        categoryId: "",
        collectionId: "",
        selectedAuthorIds: [],
        coverFile: null,
        pdfFile: null,
        epubFile: null,
      });

      if (createCoverInputRef.current) createCoverInputRef.current.value = "";
      if (createPdfInputRef.current) createPdfInputRef.current.value = "";
      if (createEpubInputRef.current) createEpubInputRef.current.value = "";

      setShowCreateForm(false);
      loadAll();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao cadastrar livro e arquivos.");
    } finally {
      setSubmitting(false);
      setUploadProgress(null);
    }
  }

  // Iniciar Edição Completa
  function startEditing(item: AdminCatalogItem) {
    setEditingItem(item);
    setEditForm({
      title: item.title,
      subtitle: item.subtitle || "",
      isbn: item.isbn || "",
      sku: item.sku,
      slug: item.slug,
      priceCents: item.price_cents,
      promoPriceCents: item.promo_price_cents || null,
      categoryId: item.category_id || "",
      collectionId: item.collection_id || "",
      selectedAuthorIds: item.authors.map((a) => a.id),
      isAvailable: item.is_available,
      coverFile: null,
      pdfFile: null,
      epubFile: null,
    });
  }

  // Salvar Edição Completa com Upload Opcional
  async function handleSaveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editingItem) return;
    setMessage(null);
    setError(null);
    setUploadProgress(null);
    setSubmitting(true);

    try {
      const priceCents = editForm.priceCents;
      const promoPriceCents = editForm.promoPriceCents ?? null;

      await apiClient.patch(`/admin/catalog/${editingItem.id}`, {
        title: editForm.title.trim(),
        subtitle: editForm.subtitle.trim() || null,
        isbn: editForm.isbn.trim() || null,
        sku: editForm.sku.trim(),
        slug: editForm.slug.trim(),
        priceCents,
        promoPriceCents,
        categoryId: editForm.categoryId || null,
        collectionId: editForm.collectionId || null,
        authorIds: editForm.selectedAuthorIds,
        isAvailable: editForm.isAvailable,
      });

      // Se houver novos arquivos para upload na edição
      if (editForm.coverFile || editForm.pdfFile || editForm.epubFile) {
        setUploadProgress("Atualizando arquivos digitais no Cloudflare R2…");
        const fd = new FormData();
        if (editForm.coverFile) fd.append("cover", editForm.coverFile);
        if (editForm.pdfFile) fd.append("pdf", editForm.pdfFile);
        if (editForm.epubFile) fd.append("epub", editForm.epubFile);

        await apiClient.postForm(`/admin/catalog/${editingItem.id}/upload`, fd);
      }

      setMessage(`Obra "${editForm.title}" atualizada com sucesso!`);
      setEditingItem(null);
      loadAll();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao atualizar obra.");
    } finally {
      setSubmitting(false);
      setUploadProgress(null);
    }
  }

  // Iniciar e Salvar Alteração Rápida de Preço
  function openPriceModal(item: AdminCatalogItem) {
    setPriceModalItem(item);
    setQuickPriceCents(item.price_cents);
    setQuickPromoPriceCents(item.promo_price_cents || null);
  }

  async function handleSaveQuickPrice(e: React.FormEvent) {
    e.preventDefault();
    if (!priceModalItem) return;
    setMessage(null);
    setError(null);
    setSubmitting(true);

    try {
      const priceCents = quickPriceCents;
      const promoPriceCents = quickPromoPriceCents ?? null;

      await apiClient.patch(`/admin/catalog/${priceModalItem.id}`, {
        priceCents,
        promoPriceCents,
      });

      setItems((prev) =>
        prev.map((it) =>
          it.id === priceModalItem.id
            ? { ...it, price_cents: priceCents, promo_price_cents: promoPriceCents }
            : it,
        ),
      );
      setMessage(`Preço de "${priceModalItem.title}" atualizado para ${formatBRL(priceCents)}.`);
      setPriceModalItem(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao alterar preço.");
    } finally {
      setSubmitting(false);
    }
  }

  // Criar Novo Autor Inline
  async function handleCreateNewAuthor(e: React.FormEvent) {
    e.preventDefault();
    if (!newAuthorName.trim()) return;
    setSubmitting(true);

    try {
      const res = await apiClient.post<AuthorItem>("/admin/authors", {
        name: newAuthorName.trim(),
        bio: newAuthorBio.trim() || undefined,
      });

      setAuthors((prev) => [...prev, res].sort((a, b) => a.name.localeCompare(b.name)));

      if (authorTarget === "create") {
        setCreateForm((prev) => ({
          ...prev,
          selectedAuthorIds: [...prev.selectedAuthorIds, res.id],
        }));
      } else {
        setEditForm((prev) => ({
          ...prev,
          selectedAuthorIds: [...prev.selectedAuthorIds, res.id],
        }));
      }

      setNewAuthorName("");
      setNewAuthorBio("");
      setShowNewAuthorModal(false);
      setMessage(`Autor "${res.name}" adicionado e selecionado!`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao cadastrar novo autor.");
    } finally {
      setSubmitting(false);
    }
  }

  // Cadastrar Nova Categoria Temática Inline
  async function handleCreateNewCategory(e: React.FormEvent) {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    setSubmitting(true);

    try {
      const res = await apiClient.post<{ id: string; name: string; slug: string }>("/admin/categories", {
        name: newCategoryName.trim(),
        parentId: newCategoryParentId || null,
      });

      setCategories((prev) => [...prev, { id: res.id, name: res.name, slug: res.slug }].sort((a, b) => a.name.localeCompare(b.name)));

      if (categoryTarget === "create") {
        setCreateForm((prev) => ({ ...prev, categoryId: res.id }));
      } else {
        setEditForm((prev) => ({ ...prev, categoryId: res.id }));
      }

      setNewCategoryName("");
      setNewCategoryParentId("");
      setShowNewCategoryModal(false);
      setMessage(`Categoria "${res.name}" cadastrada e selecionada!`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao cadastrar nova categoria.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-4)", flexWrap: "wrap", gap: "var(--space-3)" }}>
        <div>
          <h1 style={{ margin: 0 }}>Catálogo Editorial</h1>
          <p className="text-muted" style={{ margin: "var(--space-1) 0 0", fontSize: "var(--text-sm)" }}>
            Gestão de títulos técnicos, metadados de ISBN, upload de arquivos (PDF, EPUB, Capa), autores e preços.
          </p>
        </div>
        <button
          className="btn"
          onClick={() => {
            setShowCreateForm(!showCreateForm);
            setMessage(null);
            setError(null);
          }}
        >
          {showCreateForm ? "Fechar Formulário" : "+ Novo Título"}
        </button>
      </div>

      {uploadProgress && (
        <div className="card" style={{ background: "rgba(41, 128, 185, 0.1)", borderColor: "#2980b9", marginBottom: "var(--space-4)" }}>
          <p style={{ margin: 0, color: "#2980b9", fontWeight: 600 }}>
            ⏳ {uploadProgress}
          </p>
        </div>
      )}

      {message && (
        <div className="card" style={{ background: "rgba(39, 174, 96, 0.1)", borderColor: "var(--color-success)", marginBottom: "var(--space-4)" }}>
          <p role="status" style={{ margin: 0, color: "var(--color-success)", fontWeight: 500 }}>
            {message}
          </p>
        </div>
      )}

      {error && (
        <div className="card" style={{ background: "rgba(220, 38, 38, 0.1)", borderColor: "var(--color-error)", marginBottom: "var(--space-4)" }}>
          <p role="alert" style={{ margin: 0, color: "var(--color-error)", fontWeight: 500 }}>
            {error}
          </p>
        </div>
      )}

      {/* Formulário de Criação com Upload de Arquivos */}
      {showCreateForm && (
        <div className="card" style={{ marginBottom: "var(--space-6)", borderLeft: "4px solid var(--color-accent)" }}>
          <h3>Cadastrar Nova Obra no Catálogo</h3>
          <p className="text-muted" style={{ fontSize: "var(--text-sm)", margin: "var(--space-1) 0 var(--space-4)" }}>
            Preencha os metadados da obra e anexe a capa e os arquivos digitais de leitura (PDF e EPUB).
          </p>

          <form onSubmit={handleCreateBook}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "var(--space-4)" }}>
              <div className="field">
                <label htmlFor="create-title">Título da Obra *</label>
                <input
                  id="create-title"
                  required
                  placeholder="Ex: Aprendizado por Reforço Aplicado"
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                />
              </div>

              <div className="field">
                <label htmlFor="create-isbn">ISBN</label>
                <input
                  id="create-isbn"
                  placeholder="Ex: 978-65-8000-01-2"
                  value={createForm.isbn}
                  onChange={(e) => setCreateForm({ ...createForm, isbn: e.target.value })}
                />
              </div>

              <div className="field">
                <label htmlFor="create-sku">SKU (opcional, auto-gerado se vazio)</label>
                <input
                  id="create-sku"
                  placeholder="Ex: DTS-0035"
                  value={createForm.sku}
                  onChange={(e) => setCreateForm({ ...createForm, sku: e.target.value })}
                />
              </div>

              <div className="field">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-1)" }}>
                  <label htmlFor="create-category" style={{ margin: 0 }}>Catálogo / Categoria Temática</label>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ fontSize: "var(--text-xs)", padding: "2px 8px" }}
                    onClick={() => {
                      setCategoryTarget("create");
                      setShowNewCategoryModal(true);
                    }}
                  >
                    + Nova Categoria
                  </button>
                </div>
                <select
                  id="create-category"
                  value={createForm.categoryId}
                  onChange={(e) => setCreateForm({ ...createForm, categoryId: e.target.value })}
                >
                  <option value="">(Nenhuma / Geral)</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="create-collection">Coleção Editorial</label>
                <select
                  id="create-collection"
                  value={createForm.collectionId}
                  onChange={(e) => setCreateForm({ ...createForm, collectionId: e.target.value })}
                >
                  <option value="">(Nenhuma coleção)</option>
                  {collections.map((col) => (
                    <option key={col.id} value={col.id}>
                      {col.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="create-price">Preço Normal *</label>
                <CurrencyInput
                  id="create-price"
                  required
                  valueCents={createForm.priceCents}
                  onChangeCents={(cents) => setCreateForm({ ...createForm, priceCents: cents ?? 0 })}
                />
              </div>

              <div className="field">
                <label htmlFor="create-promo-price">Preço Promocional (opcional)</label>
                <CurrencyInput
                  id="create-promo-price"
                  allowEmpty
                  placeholder="0,00"
                  valueCents={createForm.promoPriceCents}
                  onChangeCents={(cents) => setCreateForm({ ...createForm, promoPriceCents: cents })}
                />
              </div>
            </div>

            {/* SEÇÃO DE ARQUIVOS DIGITAIS (PDF, EPUB, CAPA) */}
            <div style={{ marginTop: "var(--space-5)", padding: "var(--space-4)", background: "var(--color-surface-sunken)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-border)" }}>
              <h4 style={{ margin: "0 0 var(--space-3)", color: "var(--color-brass)" }}>Arquivos Digitais & Capa da Obra</h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "var(--space-4)" }}>
                <div className="field">
                  <label htmlFor="create-cover-file" style={{ fontWeight: 600 }}>
                    Capa da Obra (PNG, JPG ou SVG)
                  </label>
                  <input
                    id="create-cover-file"
                    ref={createCoverInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
                    onChange={(e) => setCreateForm({ ...createForm, coverFile: e.target.files?.[0] || null })}
                    style={{ fontSize: "var(--text-xs)" }}
                  />
                  <span className="text-muted" style={{ fontSize: "11px" }}>Formatos aceitos: PNG, JPG ou SVG.</span>
                </div>

                <div className="field">
                  <label htmlFor="create-pdf-file" style={{ fontWeight: 600 }}>
                    E-book em PDF (.pdf)
                  </label>
                  <input
                    id="create-pdf-file"
                    ref={createPdfInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={(e) => setCreateForm({ ...createForm, pdfFile: e.target.files?.[0] || null })}
                    style={{ fontSize: "var(--text-xs)" }}
                  />
                  <span className="text-muted" style={{ fontSize: "11px" }}>Arquivo do livro digital no formato PDF.</span>
                </div>

                <div className="field">
                  <label htmlFor="create-epub-file" style={{ fontWeight: 600 }}>
                    E-book em EPUB (.epub)
                  </label>
                  <input
                    id="create-epub-file"
                    ref={createEpubInputRef}
                    type="file"
                    accept=".epub,application/epub+zip"
                    onChange={(e) => setCreateForm({ ...createForm, epubFile: e.target.files?.[0] || null })}
                    style={{ fontSize: "var(--text-xs)" }}
                  />
                  <span className="text-muted" style={{ fontSize: "11px" }}>Arquivo do livro digital no formato EPUB.</span>
                </div>
              </div>
            </div>

            {/* Seleção de Autores */}
            <div style={{ marginTop: "var(--space-4)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-2)" }}>
                <label style={{ fontWeight: 600 }}>Autores da Obra:</label>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: "var(--text-xs)", padding: "0.25rem 0.6rem" }}
                  onClick={() => {
                    setAuthorTarget("create");
                    setShowNewAuthorModal(true);
                  }}
                >
                  + Cadastrar Novo Autor
                </button>
              </div>

              {authors.length === 0 ? (
                <p className="text-muted" style={{ fontSize: "var(--text-xs)" }}>
                  Nenhum autor cadastrado ainda. Clique acima para cadastrar o primeiro.
                </p>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)", maxHeight: 140, overflowY: "auto", padding: "var(--space-2)", background: "var(--color-surface-sunken)", borderRadius: "var(--radius-sm)" }}>
                  {authors.map((author) => {
                    const isSelected = createForm.selectedAuthorIds.includes(author.id);
                    return (
                      <button
                        key={author.id}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            setCreateForm({
                              ...createForm,
                              selectedAuthorIds: createForm.selectedAuthorIds.filter((id) => id !== author.id),
                            });
                          } else {
                            setCreateForm({
                              ...createForm,
                              selectedAuthorIds: [...createForm.selectedAuthorIds, author.id],
                            });
                          }
                        }}
                        style={{
                          padding: "0.3rem 0.65rem",
                          borderRadius: "var(--radius-sm)",
                          border: isSelected ? "1px solid var(--color-brass)" : "1px solid var(--color-border)",
                          background: isSelected ? "var(--color-accent-soft)" : "var(--color-surface-elevated)",
                          color: isSelected ? "var(--color-accent)" : "var(--color-text)",
                          fontSize: "var(--text-xs)",
                          cursor: "pointer",
                          fontWeight: isSelected ? 600 : 400,
                        }}
                      >
                        {isSelected ? "✓ " : "+ "} {author.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-5)" }}>
              <button className="btn" type="submit" disabled={submitting}>
                {submitting ? "Cadastrando e enviando…" : "Salvar no Catálogo com Arquivos"}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreateForm(false)}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Barra de Pesquisa e Filtros */}
      <div className="card" style={{ marginBottom: "var(--space-5)", display: "flex", gap: "var(--space-4)", flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: "1 1 280px" }}>
          <label htmlFor="search-catalog" style={{ fontSize: "var(--text-xs)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)" }}>
            Pesquisar no Catálogo:
          </label>
          <input
            id="search-catalog"
            type="search"
            placeholder="Buscar por título, autor ou ISBN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: "100%", marginTop: "var(--space-1)" }}
          />
        </div>

        <div>
          <label style={{ fontSize: "var(--text-xs)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", display: "block" }}>
            Disponibilidade:
          </label>
          <div role="tablist" className="tab-group" style={{ marginTop: "var(--space-1)" }}>
            <button role="tab" aria-selected={statusFilter === "all"} onClick={() => setStatusFilter("all")}>
              Todos ({items.length})
            </button>
            <button role="tab" aria-selected={statusFilter === "active"} onClick={() => setStatusFilter("active")}>
              Ativos ({items.filter((i) => i.is_available).length})
            </button>
            <button role="tab" aria-selected={statusFilter === "inactive"} onClick={() => setStatusFilter("inactive")}>
              Inativos ({items.filter((i) => !i.is_available).length})
            </button>
          </div>
        </div>
      </div>

      {/* Tabela do Catálogo */}
      <div className="card" style={{ overflowX: "auto" }}>
        {loading ? (
          <p className="text-muted" style={{ padding: "var(--space-4)" }}>Carregando catálogo editorial…</p>
        ) : filteredItems.length === 0 ? (
          <p className="empty-state" style={{ padding: "var(--space-6)" }}>
            Nenhum título encontrado com os filtros atuais.
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Obra / Metadados</th>
                <th>Arquivos Digitais</th>
                <th>Autor(es)</th>
                <th>Catálogo / Coleção</th>
                <th className="num">Valor</th>
                <th style={{ textAlign: "center" }}>Status</th>
                <th style={{ textAlign: "right" }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {catalogPagination.paginatedItems.map((item) => {
                const hasPdf = item.formats.includes("pdf");
                const hasEpub = item.formats.includes("epub");
                const hasCover = Boolean(item.cover_r2_key);

                return (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.title}</strong>
                      {item.subtitle && <p className="text-muted" style={{ fontSize: "var(--text-xs)", margin: 0 }}>{item.subtitle}</p>}
                      <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-1)", fontSize: "var(--text-xs)" }}>
                        <span className="text-muted">SKU: <code>{item.sku}</code></span>
                        {item.isbn ? (
                          <span style={{ color: "var(--color-accent)", fontWeight: 500 }}>ISBN: {item.isbn}</span>
                        ) : (
                          <span className="text-muted">ISBN: —</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                        {hasCover && (
                          <span style={{ background: "rgba(39, 174, 96, 0.15)", color: "#1b7340", padding: "2px 6px", borderRadius: "3px", fontSize: "10px", fontWeight: 600 }}>
                            Capa
                          </span>
                        )}
                        {hasPdf && (
                          <span style={{ background: "rgba(41, 128, 185, 0.15)", color: "#2980b9", padding: "2px 6px", borderRadius: "3px", fontSize: "10px", fontWeight: 600 }}>
                            PDF
                          </span>
                        )}
                        {hasEpub && (
                          <span style={{ background: "rgba(142, 68, 173, 0.15)", color: "#8e44ad", padding: "2px 6px", borderRadius: "3px", fontSize: "10px", fontWeight: 600 }}>
                            EPUB
                          </span>
                        )}
                        {!hasCover && !hasPdf && !hasEpub && (
                          <span className="text-muted" style={{ fontSize: "11px" }}>Pendente</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {item.authors.length > 0 ? (
                        <span style={{ fontSize: "var(--text-sm)" }}>
                          {item.authors.map((a) => a.name).join(", ")}
                        </span>
                      ) : (
                        <span className="text-muted" style={{ fontSize: "var(--text-xs)" }}>Sem autor</span>
                      )}
                    </td>
                    <td>
                      <span style={{ display: "block", fontSize: "var(--text-sm)", fontWeight: 500 }}>
                        {item.category_name || "Sem categoria"}
                      </span>
                      {item.collection_title && (
                        <span className="text-muted" style={{ fontSize: "var(--text-xs)" }}>
                          Col: {item.collection_title}
                        </span>
                      )}
                    </td>
                    <td className="num">
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                        <span style={{ fontWeight: 600, fontSize: "var(--text-sm)" }}>
                          {formatBRL(item.promo_price_cents ?? item.price_cents)}
                        </span>
                        {item.promo_price_cents && (
                          <span className="text-muted" style={{ textDecoration: "line-through", fontSize: "var(--text-xs)" }}>
                            {formatBRL(item.price_cents)}
                          </span>
                        )}
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: "10px", padding: "2px 6px", marginTop: "4px" }}
                          onClick={() => openPriceModal(item)}
                          title="Alterar valor da obra"
                        >
                          Alterar valor
                        </button>
                      </div>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        onClick={() => handleToggleStatus(item)}
                        style={{
                          padding: "4px 10px",
                          borderRadius: "var(--radius-sm)",
                          border: "none",
                          cursor: "pointer",
                          fontWeight: 600,
                          fontSize: "var(--text-xs)",
                          background: item.is_available ? "rgba(39, 174, 96, 0.15)" : "rgba(192, 57, 43, 0.15)",
                          color: item.is_available ? "#1b7340" : "#c0392b",
                          transition: "all 0.2s ease",
                        }}
                        title={item.is_available ? "Clique para desativar obra" : "Clique para ativar obra"}
                      >
                        {item.is_available ? "● Ativo" : "○ Inativo"}
                      </button>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: "var(--text-xs)", padding: "0.3rem 0.6rem" }}
                        onClick={() => startEditing(item)}
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <Pagination
          currentPage={catalogPagination.currentPage}
          totalItems={catalogPagination.totalItems}
          pageSize={catalogPagination.pageSize}
          onPageChange={catalogPagination.setCurrentPage}
          itemLabel="títulos"
        />
      </div>

      {/* Modal de Alteração Rápida de Preço */}
      {priceModalItem && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "var(--space-4)" }}>
          <div className="card" style={{ maxWidth: 420, width: "100%", background: "var(--color-surface)", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.3)" }}>
            <h3>Alterar Valor da Obra</h3>
            <p style={{ fontWeight: 600, color: "var(--color-accent)", margin: "var(--space-1) 0 var(--space-4)" }}>
              {priceModalItem.title}
            </p>

            <form onSubmit={handleSaveQuickPrice}>
              <div className="field">
                <label htmlFor="quick-price">Preço Normal *</label>
                <CurrencyInput
                  id="quick-price"
                  required
                  valueCents={quickPriceCents}
                  onChangeCents={(cents) => setQuickPriceCents(cents ?? 0)}
                />
              </div>

              <div className="field" style={{ marginTop: "var(--space-3)" }}>
                <label htmlFor="quick-promo-price">Preço Promocional (opcional, deixe vazio para remover)</label>
                <CurrencyInput
                  id="quick-promo-price"
                  allowEmpty
                  placeholder="0,00"
                  valueCents={quickPromoPriceCents}
                  onChangeCents={(cents) => setQuickPromoPriceCents(cents)}
                />
              </div>

              <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-5)" }}>
                <button className="btn" type="submit" disabled={submitting}>
                  {submitting ? "Salvando…" : "Salvar Preço"}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setPriceModalItem(null)}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Edição Completa da Obra com Upload de Arquivos */}
      {editingItem && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "var(--space-4)", overflowY: "auto" }}>
          <div className="card" style={{ maxWidth: 680, width: "100%", maxHeight: "90vh", overflowY: "auto", background: "var(--color-surface)", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.3)" }}>
            <h3>Editar Obra & Arquivos Digitais</h3>
            <p className="text-muted" style={{ margin: "var(--space-1) 0 var(--space-4)", fontSize: "var(--text-sm)" }}>
              Atualize as informações cadastrais, ISBN, catálogo, autores e substitua ou anexe arquivos R2.
            </p>

            <form onSubmit={handleSaveEdit}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "var(--space-3)" }}>
                <div className="field">
                  <label htmlFor="edit-title">Título *</label>
                  <input
                    id="edit-title"
                    required
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  />
                </div>

                <div className="field">
                  <label htmlFor="edit-isbn">ISBN</label>
                  <input
                    id="edit-isbn"
                    placeholder="978-65-..."
                    value={editForm.isbn}
                    onChange={(e) => setEditForm({ ...editForm, isbn: e.target.value })}
                  />
                </div>

                <div className="field">
                  <label htmlFor="edit-sku">SKU *</label>
                  <input
                    id="edit-sku"
                    required
                    value={editForm.sku}
                    onChange={(e) => setEditForm({ ...editForm, sku: e.target.value })}
                  />
                </div>

                <div className="field">
                  <label htmlFor="edit-slug">Slug de URL *</label>
                  <input
                    id="edit-slug"
                    required
                    value={editForm.slug}
                    onChange={(e) => setEditForm({ ...editForm, slug: e.target.value })}
                  />
                </div>

                <div className="field">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-1)" }}>
                    <label htmlFor="edit-category" style={{ margin: 0 }}>Catálogo / Categoria</label>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ fontSize: "var(--text-xs)", padding: "2px 8px" }}
                      onClick={() => {
                        setCategoryTarget("edit");
                        setShowNewCategoryModal(true);
                      }}
                    >
                      + Nova Categoria
                    </button>
                  </div>
                  <select
                    id="edit-category"
                    value={editForm.categoryId}
                    onChange={(e) => setEditForm({ ...editForm, categoryId: e.target.value })}
                  >
                    <option value="">(Nenhuma)</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label htmlFor="edit-collection">Coleção</label>
                  <select
                    id="edit-collection"
                    value={editForm.collectionId}
                    onChange={(e) => setEditForm({ ...editForm, collectionId: e.target.value })}
                  >
                    <option value="">(Nenhuma)</option>
                    {collections.map((col) => (
                      <option key={col.id} value={col.id}>
                        {col.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label htmlFor="edit-price">Preço Normal *</label>
                  <CurrencyInput
                    id="edit-price"
                    required
                    valueCents={editForm.priceCents}
                    onChangeCents={(cents) => setEditForm({ ...editForm, priceCents: cents ?? 0 })}
                  />
                </div>

                <div className="field">
                  <label htmlFor="edit-promo-price">Preço Promocional (opcional)</label>
                  <CurrencyInput
                    id="edit-promo-price"
                    allowEmpty
                    placeholder="Deixe vazio para desativar"
                    valueCents={editForm.promoPriceCents}
                    onChangeCents={(cents) => setEditForm({ ...editForm, promoPriceCents: cents })}
                  />
                </div>
              </div>

              {/* SEÇÃO DE ARQUIVOS NO MODAL DE EDIÇÃO */}
              <div style={{ marginTop: "var(--space-4)", padding: "var(--space-3)", background: "var(--color-surface-sunken)", borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border)" }}>
                <h4 style={{ margin: "0 0 var(--space-2)", fontSize: "var(--text-sm)", color: "var(--color-brass)" }}>
                  Arquivos Digitais & Capa (R2)
                </h4>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "var(--space-3)" }}>
                  <div className="field">
                    <label htmlFor="edit-cover-file" style={{ fontSize: "var(--text-xs)", fontWeight: 600 }}>
                      Capa (PNG, JPG, SVG) {editingItem.cover_r2_key ? "✓ Cadastrada" : "○ Ausente"}
                    </label>
                    <input
                      id="edit-cover-file"
                      ref={editCoverInputRef}
                      type="file"
                      accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
                      onChange={(e) => setEditForm({ ...editForm, coverFile: e.target.files?.[0] || null })}
                      style={{ fontSize: "var(--text-xs)" }}
                    />
                    <span className="text-muted" style={{ fontSize: "10px" }}>Selecione para substituir capa.</span>
                  </div>

                  <div className="field">
                    <label htmlFor="edit-pdf-file" style={{ fontSize: "var(--text-xs)", fontWeight: 600 }}>
                      PDF {editingItem.formats.includes("pdf") ? "✓ Disponível" : "○ Ausente"}
                    </label>
                    <input
                      id="edit-pdf-file"
                      ref={editPdfInputRef}
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={(e) => setEditForm({ ...editForm, pdfFile: e.target.files?.[0] || null })}
                      style={{ fontSize: "var(--text-xs)" }}
                    />
                    <span className="text-muted" style={{ fontSize: "10px" }}>Selecione para substituir PDF.</span>
                  </div>

                  <div className="field">
                    <label htmlFor="edit-epub-file" style={{ fontSize: "var(--text-xs)", fontWeight: 600 }}>
                      EPUB {editingItem.formats.includes("epub") ? "✓ Disponível" : "○ Ausente"}
                    </label>
                    <input
                      id="edit-epub-file"
                      ref={editEpubInputRef}
                      type="file"
                      accept=".epub,application/epub+zip"
                      onChange={(e) => setEditForm({ ...editForm, epubFile: e.target.files?.[0] || null })}
                      style={{ fontSize: "var(--text-xs)" }}
                    />
                    <span className="text-muted" style={{ fontSize: "10px" }}>Selecione para substituir EPUB.</span>
                  </div>
                </div>
              </div>

              <div className="field" style={{ marginTop: "var(--space-3)" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={editForm.isAvailable}
                    onChange={(e) => setEditForm({ ...editForm, isAvailable: e.target.checked })}
                  />
                  <span>Obra ativa e disponível para venda no catálogo público</span>
                </label>
              </div>

              {/* Autores no modal de edição */}
              <div style={{ marginTop: "var(--space-4)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-2)" }}>
                  <label style={{ fontWeight: 600 }}>Autores:</label>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ fontSize: "var(--text-xs)", padding: "0.2rem 0.5rem" }}
                    onClick={() => {
                      setAuthorTarget("edit");
                      setShowNewAuthorModal(true);
                    }}
                  >
                    + Novo Autor
                  </button>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)", maxHeight: 120, overflowY: "auto", padding: "var(--space-2)", background: "var(--color-surface-sunken)", borderRadius: "var(--radius-sm)" }}>
                  {authors.map((author) => {
                    const isSelected = editForm.selectedAuthorIds.includes(author.id);
                    return (
                      <button
                        key={author.id}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            setEditForm({
                              ...editForm,
                              selectedAuthorIds: editForm.selectedAuthorIds.filter((id) => id !== author.id),
                            });
                          } else {
                            setEditForm({
                              ...editForm,
                              selectedAuthorIds: [...editForm.selectedAuthorIds, author.id],
                            });
                          }
                        }}
                        style={{
                          padding: "0.25rem 0.5rem",
                          borderRadius: "var(--radius-sm)",
                          border: isSelected ? "1px solid var(--color-brass)" : "1px solid var(--color-border)",
                          background: isSelected ? "var(--color-accent-soft)" : "var(--color-surface-elevated)",
                          color: isSelected ? "var(--color-accent)" : "var(--color-text)",
                          fontSize: "var(--text-xs)",
                          cursor: "pointer",
                          fontWeight: isSelected ? 600 : 400,
                        }}
                      >
                        {isSelected ? "✓ " : "+ "} {author.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-5)" }}>
                <button className="btn" type="submit" disabled={submitting}>
                  {submitting ? "Salvando…" : "Salvar Alterações"}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setEditingItem(null)}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para Criar Novo Autor */}
      {showNewAuthorModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: "var(--space-4)" }}>
          <div className="card" style={{ maxWidth: 420, width: "100%", background: "var(--color-surface)", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)" }}>
            <h3>Cadastrar Novo Autor</h3>
            <p className="text-muted" style={{ margin: "var(--space-1) 0 var(--space-4)", fontSize: "var(--text-sm)" }}>
              O autor será criado e automaticamente vinculado à obra selecionada.
            </p>

            <form onSubmit={handleCreateNewAuthor}>
              <div className="field">
                <label htmlFor="new-author-name">Nome Completo do Autor *</label>
                <input
                  id="new-author-name"
                  required
                  placeholder="Ex: Leonardo Guedes"
                  value={newAuthorName}
                  onChange={(e) => setNewAuthorName(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="field" style={{ marginTop: "var(--space-3)" }}>
                <label htmlFor="new-author-bio">Biografia Curta (opcional)</label>
                <textarea
                  id="new-author-bio"
                  rows={3}
                  placeholder="Especialista em Machine Learning e Engenharia de Dados..."
                  value={newAuthorBio}
                  onChange={(e) => setNewAuthorBio(e.target.value)}
                />
              </div>

              <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-5)" }}>
                <button className="btn" type="submit" disabled={submitting}>
                  {submitting ? "Adicionando…" : "Cadastrar e Vincular"}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowNewAuthorModal(false)}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para Criar Nova Categoria Temática Inline */}
      {showNewCategoryModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: "var(--space-4)" }}>
          <div className="card" style={{ maxWidth: 420, width: "100%", background: "var(--color-surface)", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)" }}>
            <h3>Cadastrar Nova Categoria Temática</h3>
            <p className="text-muted" style={{ margin: "var(--space-1) 0 var(--space-4)", fontSize: "var(--text-sm)" }}>
              A nova categoria temática será registrada no acervo e selecionada para este título.
            </p>

            <form onSubmit={handleCreateNewCategory}>
              <div className="field">
                <label htmlFor="new-category-name">Nome da Categoria *</label>
                <input
                  id="new-category-name"
                  required
                  placeholder="Ex: Computação Quântica"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="field" style={{ marginTop: "var(--space-3)" }}>
                <label htmlFor="new-category-parent">Categoria Pai (Opcional)</label>
                <select
                  id="new-category-parent"
                  value={newCategoryParentId}
                  onChange={(e) => setNewCategoryParentId(e.target.value)}
                >
                  <option value="">(Nenhuma - Categoria Principal)</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-5)" }}>
                <button className="btn" type="submit" disabled={submitting}>
                  {submitting ? "Cadastrando…" : "Cadastrar e Selecionar"}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowNewCategoryModal(false)}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
